<template>
    <div>
        <ts-page-title
            :title="$t('devicePostingLogV3.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('devicePostingLogV3.pageTitle'), active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i
                            class="fas tw-text-gray-400"
                            :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                            style="width: 12px"
                        ></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span
                                v-for="chip in activeChips"
                                :key="chip.key"
                                class="chip-pill"
                                :class="chip.tone || 'chip-default'"
                            >
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Date (preset + inline custom) + Tran Type -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 7">
                            <label class="filter-lbl">Date Range</label>
                            <div class="tw-flex tw-items-stretch tw-gap-2">
                                <div class="pill-group">
                                    <button v-for="p in datePresets"
                                            :key="p.value"
                                            type="button"
                                            class="pill"
                                            :class="{ 'pill-active': model.date_preset === p.value }"
                                            @click="model.date_preset = p.value">{{ p.label }}</button>
                                </div>
                                <div v-if="isCustomRange" class="tw-flex-1">
                                    <DatePicker
                                        type="daterange"
                                        format="dd-MM-yyyy"
                                        placeholder="Select range (max 31 days)"
                                        style="width: 100%"
                                        @on-change="onCustomRangeChange"
                                    ></DatePicker>
                                </div>
                                <div v-else class="date-preview">
                                    {{ presetPreview }}
                                </div>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 5">
                            <label class="filter-lbl">Transaction Type</label>
                            <div class="pill-group">
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.tran_id === null }"
                                    @click="model.tran_id = null">All</button>
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.tran_id === 11 }"
                                    @click="model.tran_id = 11">Repayment</button>
                                <button type="button" class="pill"
                                    :class="{ 'pill-active': model.tran_id === 17 }"
                                    @click="model.tran_id = 17">Recovery</button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2: Branch + Users + Search -->
                    <div class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Branch <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span></label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>
                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">User Login</label>
                            <Select
                                v-model="model.user_id"
                                multiple
                                :max-tag-count="1"
                                :filterable="true"
                                :filter-by-label="true"
                                placeholder="All users"
                                @on-query-change="onUserQueryChange"
                            >
                                <Option
                                    v-for="item in visibleUsers"
                                    :value="item.user_id"
                                    :key="item.user_id"
                                    :label="item.user_name"
                                >
                                    {{ item.user_name }}
                                    <span v-if="item.employee" class="tw-text-gray-400">
                                        | {{ item.employee.employee_name_en }}
                                    </span>
                                </Option>
                            </Select>
                            <div class="text-danger tw-text-xs tw-mt-1" v-if="errors.has('user_id')">
                                {{ errors.first('user_id') }}
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 5">
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input type="text" class="form-control search-input"
                                        v-model="searchQuery"
                                        placeholder="Loan #, customer code / name, user name"
                                        @keyup.enter="onSearch"/>
                                    <button v-if="searchQuery" type="button" class="search-clear"
                                        title="Clear search" @click.prevent="onClearSearch">
                                        <i class="fas fa-times-circle"></i>
                                    </button>
                                </div>
                                <button type="button" class="search-btn" :disabled="loading" @click.prevent="onSearch">
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button type="button" class="reset-btn" @click.prevent="onReset" title="Reset all filters">
                                    <i class="fas fa-undo"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div
                v-for="card in summaryCards"
                :key="card.key"
                class="summary-card"
                :class="'summary-card--' + card.tone"
            >
                <div class="summary-card__header">
                    <div class="summary-card__icon"><i :class="'fas ' + card.icon"></i></div>
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div v-if="card.badge !== undefined" class="summary-card__count">
                        <template v-if="summaryLoading">…</template>
                        <template v-else>{{ card.badge }}</template>
                    </div>
                </div>
                <div class="summary-card__value">
                    <template v-if="summaryLoading">
                        <span class="tw-text-gray-300">—</span>
                    </template>
                    <template v-else>
                        <span class="summary-card__amount">{{ card.formatted }}</span>
                        <span class="summary-card__currency">{{ card.unit }}</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…
                        </span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400">
                            <i class="fas fa-info-circle tw-mr-1"></i> No search yet
                        </span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500">
                            <i class="far fa-folder-open tw-mr-1"></i> No results
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} rows
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>
                </div>

                <div class="export-group" title="Export the current filters to xlsx">
                    <select v-model="model.excel_template" class="export-select">
                        <option value="standard">Standard xlsx</option>
                        <option value="raw-data">Raw Data xlsx</option>
                    </select>
                    <button
                        type="button"
                        class="export-btn"
                        :disabled="exporting"
                        @click.prevent="exportExcel"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                        <i class="far fa-file-excel" v-else></i>
                        <span class="tw-ml-1">Export</span>
                    </button>
                </div>
            </div>

            <div
                class="results-body tw-relative"
                :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }"
            >
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">Ready when you are — click <b>Search</b> above.</div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">No device posting log entries for the selected filters.</div>
                </div>

                <Table
                    v-else
                    :columns="columns"
                    :data="rows"
                    border
                    stripe
                    size="small"
                    :height="tableHeight"
                    show-summary
                    :summary-method="summaryMethod"
                    @on-sort-change="onSortChange"
                ></Table>
            </div>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select
                        v-model="perPage"
                        @on-change="onPerPageChange"
                        style="width: 80px"
                        size="small"
                    >
                        <Option :value="10">10</Option>
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page
                    :current="meta.current_page"
                    :total="meta.total || 0"
                    :page-size="perPage"
                    @on-change="onPageChange"
                    size="small"
                    show-elevator
                ></Page>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce, trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

export default {
    name: 'devicePostingLogV3',
    data () {
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            errors: new Errors(),
            searchQuery: '',
            perPage: 30,
            viewportHeight: window.innerHeight,
            summaryLoading: false,
            userQuery: '',
            summary: {
                all:       { count: 0, total: '0' },
                repayment: { count: 0, total: '0' },
                recovery:  { count: 0, total: '0' },
                users:     { count: 0 }
            },
            totals: {
                actual_payment_amount: '0',
                principal:             '0',
                principal_paid:        '0',
                principal_due:         '0',
                interest:              '0',
                interest_paid:         '0',
                interest_due:          '0'
            },
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            datePresets: [
                { value: 'today',      label: 'Today' },
                { value: 'yesterday',  label: 'Yesterday' },
                { value: 'custom',     label: 'Custom' }
            ],
            model: {
                date_preset: 'today',
                date_from: null,
                date_to: null,
                branch_id: [],
                user_id: [],
                tran_id: null,
                search: null,
                sort: 'transaction_date',
                sort_dir: 'desc',
                page: 1,
                perPage: 30,
                excel_template: 'standard'
            }
        }
    },
    computed: {
        ...mapState('report/resource', ['users']),

        isKh () {
            const locale = (this.$i18n && this.$i18n.locale) || localStorage.getItem('langCode')
            return locale === 'kh'
        },
        isCustomRange () {
            return this.model.date_preset === 'custom'
        },

        /**
         * Cap the user dropdown at 100 options. iView's :filterable filters
         * what's rendered against the typed query (user_name via :label), so
         * we filter the full `users` list ourselves against `userQuery` and
         * slice to 100 — otherwise cross-branch admins see 1k+ options.
         *
         * Always include any currently-selected user_ids so their chip labels
         * stay resolved even when scrolled past the top 100.
         */
        visibleUsers () {
            const LIMIT = 100
            const all = this.users || []
            const q   = (this.userQuery || '').trim().toLowerCase()

            const matches = q
                ? all.filter(u => {
                    const name = (u.user_name || '').toLowerCase()
                    const emp  = u.employee && (u.employee.employee_name_en || '')
                    return name.includes(q) || (emp && emp.toLowerCase().includes(q))
                })
                : all

            const sliced   = matches.slice(0, LIMIT)
            const selected = new Set(this.model.user_id || [])

            if (!selected.size) return sliced

            const includedIds = new Set(sliced.map(u => u.user_id))
            const pinned = all.filter(u => selected.has(u.user_id) && !includedIds.has(u.user_id))
            return pinned.length ? pinned.concat(sliced) : sliced
        },

        presetPreview () {
            const today = moment()
            switch (this.model.date_preset) {
                case 'today':     return today.format('ddd, DD MMM YYYY')
                case 'yesterday': return today.clone().subtract(1, 'day').format('ddd, DD MMM YYYY')
                case 'custom':
                    if (this.model.date_from && this.model.date_to) {
                        return this.model.date_from + ' → ' + this.model.date_to
                    }
                    return null
            }
            return ''
        },

        activeChips () {
            const chips = []
            if (this.presetPreview) {
                chips.push({ key: 'date', icon: 'fa-calendar-alt', label: this.presetPreview, tone: 'chip-branch' })
            }
            if (this.model.branch_id && this.model.branch_id.length) {
                chips.push({ key: 'branch', icon: 'fa-building',
                    label: this.model.branch_id.length + ' branch' + (this.model.branch_id.length > 1 ? 'es' : ''),
                    tone: 'chip-branch' })
            }
            if (this.model.tran_id === 11)      chips.push({ key: 'tran', icon: 'fa-filter', label: 'Repayment only', tone: 'chip-type' })
            else if (this.model.tran_id === 17) chips.push({ key: 'tran', icon: 'fa-filter', label: 'Recovery only',  tone: 'chip-type' })
            if (this.model.user_id && this.model.user_id.length) {
                chips.push({ key: 'users', icon: 'fa-user',
                    label: this.model.user_id.length + ' user' + (this.model.user_id.length > 1 ? 's' : ''),
                    tone: 'chip-co' })
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                chips.push({ key: 'search', icon: 'fa-search', label: '"' + this.searchQuery.trim() + '"', tone: 'chip-search' })
            }
            return chips
        },

        tableHeight () {
            const chrome = this.filtersOpen ? 601 : 471
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            const PAGINATION_ROW = 52
            return this.tableHeight + PAGINATION_ROW
        },
        hasRows () {
            return !this.loading && this.meta.total > 0
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },

        summaryCards () {
            const s = this.summary
            return [
                {
                    key: 'all', tone: 'cur_due', icon: 'fa-list-check',
                    label: 'All Entries',
                    badge: (s.all.count || 0).toLocaleString(),
                    formatted: this.fmt(s.all.total),
                    unit: ''
                },
                {
                    key: 'rep', tone: 'cur_paid', icon: 'fa-money-bill',
                    label: 'Repayment',
                    badge: (s.repayment.count || 0).toLocaleString(),
                    formatted: this.fmt(s.repayment.total),
                    unit: ''
                },
                {
                    key: 'rec', tone: 'early', icon: 'fa-hand-holding-usd',
                    label: 'Recovery',
                    badge: (s.recovery.count || 0).toLocaleString(),
                    formatted: this.fmt(s.recovery.total),
                    unit: ''
                },
                {
                    key: 'users', tone: 'past_due', icon: 'fa-users',
                    label: 'Users',
                    formatted: (s.users.count || 0).toLocaleString(),
                    unit: 'distinct'
                }
            ]
        },

        columns () {
            return [
                {
                    title: 'Date / Time', key: 'transaction_date', width: 160, fixed: 'left', sortable: 'custom',
                    render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-700' },
                        params.row.transaction_date ? moment(params.row.transaction_date).format('DD-MMM-YY HH:mm:ss') : '—')
                },
                { title: 'Type', key: 'tran_id', width: 110, align: 'center', sortable: 'custom',
                  render: (h, params) => this.tranBadge(params.row) },
                {
                    title: 'Loan #', key: 'loan_number', width: 140, fixed: 'left', sortable: 'custom',
                    render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' },
                        params.row.loan_account_no || params.row.loan_number || '—')
                },
                { title: 'Cust Code', key: 'customer_code', width: 110,
                  render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.customer_code || '—') },
                { title: 'Customer', key: 'customer_name_en', minWidth: 180, sortable: 'custom',
                  render: (h, params) => h('span', { class: 'name-text' }, this.isKh
                      ? (params.row.customer_name_kh || params.row.customer_name_en || '—')
                      : (params.row.customer_name_en || params.row.customer_name_kh || '—')) },
                { title: 'Cycle Date', key: 'cycle_payment_date', width: 110, align: 'center',
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                      params.row.cycle_payment_date ? moment(params.row.cycle_payment_date).format('DD-MMM-YY') : '—') },
                { title: 'Actual Date', key: 'actual_payment_date', width: 110, align: 'center',
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' },
                      params.row.actual_payment_date ? moment(params.row.actual_payment_date).format('DD-MMM-YY') : '—') },
                { title: 'Payment Amt', key: 'actual_payment_amount', width: 140, align: 'right', sortable: 'custom',
                  render: (h, params) => this.moneyCell(params.row.actual_payment_amount) },
                // Principal columns
                { title: 'Principal', key: 'principal', width: 130, align: 'right',
                  render: (h, params) => this.moneyCell(params.row.principal) },
                { title: 'Principal Paid', key: 'principal_paid', width: 140, align: 'right',
                  render: (h, params) => this.moneyCell(params.row.principal_paid) },
                { title: 'Principal Due', key: 'principal_due', width: 140, align: 'right',
                  render: (h, params) => this.moneyCell(params.row.principal_due) },
                // Interest columns
                { title: 'Interest', key: 'interest', width: 120, align: 'right',
                  render: (h, params) => this.moneyCell(params.row.interest) },
                { title: 'Interest Paid', key: 'interest_paid', width: 130, align: 'right',
                  render: (h, params) => this.moneyCell(params.row.interest_paid) },
                { title: 'Interest Due', key: 'interest_due', width: 130, align: 'right',
                  render: (h, params) => this.moneyCell(params.row.interest_due) },
                { title: 'User', key: 'user_name', width: 130, sortable: 'custom',
                  render: (h, params) => h('span', { class: 'name-text' }, params.row.user_name || '—') },
                // Device breakdown — matches v2 Excel columns Q..T
                { title: 'Brand', key: 'brand', width: 110,
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-700' }, params.row.brand || '—') },
                { title: 'Model', key: 'model', width: 140,
                  render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-700' }, params.row.model || '—') },
                { title: 'Identifier', key: 'identifier', minWidth: 160,
                  render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.identifier || '—') },
                { title: 'Physical', key: 'isphysicaldevice', width: 90, align: 'center',
                  render: (h, params) => this.physicalBadge(params.row) }
            ]
        }
    },
    methods: {
        ...mapActions('report/resource', ['getUserLogin']),
        ...mapActions('auth/user', ['getBranchAssigned']),

        /**
         * Scope the user-login dropdown to users whose employee profile sits in
         * one of the signed-in operator's assigned branches. Falls back to the
         * unfiltered call if branch resolution fails — the backend still
         * authorises the report action, this is UX scoping.
         */
        loadUsersScoped () {
            return this.getBranchAssigned()
                .then(res => {
                    const branches = Array.isArray(res) ? res
                        : (res && Array.isArray(res.data)) ? res.data
                        : []
                    const branchIds = branches
                        .map(b => (b && (b.branch_id || b.id)) || null)
                        .filter(Boolean)
                    const params = branchIds.length ? { branch_id: branchIds } : {}
                    return this.getUserLogin(params)
                })
                .catch(() => this.getUserLogin())
        },

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        fmtStrict (v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '0'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        moneyCell (val) {
            const h = this.$createElement
            const n = parseFloat(val)
            if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
            return h('span', this.fmt(val))
        },
        tranBadge (row) {
            const h = this.$createElement
            if (row.tran_id === 11) {
                return h('span', { class: 'tran-badge tran-badge--rep' }, 'Repayment')
            }
            if (row.tran_id === 17) {
                return h('span', { class: 'tran-badge tran-badge--rec' }, 'Recovery')
            }
            return h('span', '—')
        },
        physicalBadge (row) {
            const h = this.$createElement
            if (row.isphysicaldevice === true) {
                return h('span', { class: 'phys-badge phys-badge--yes' }, 'Yes')
            }
            if (row.isphysicaldevice === false) {
                return h('span', { class: 'phys-badge phys-badge--no' }, 'Emulator')
            }
            return h('span', { class: 'tw-text-gray-300' }, '—')
        },

        summaryMethod ({ columns, data }) {
            const sums = {}
            const keys = [
                'actual_payment_amount',
                'principal', 'principal_paid', 'principal_due',
                'interest',  'interest_paid',  'interest_due'
            ]
            const pageTotals = Object.fromEntries(keys.map(k => [k, 0]))
            for (const row of (data || [])) {
                for (const k of keys) {
                    pageTotals[k] += parseFloat(row[k]) || 0
                }
            }
            columns.forEach((col, idx) => {
                if (idx === 0) { sums[col.key] = { key: col.key, value: 'PAGE TOTAL' }; return }
                const value = keys.includes(col.key) ? this.fmtStrict(pageTotals[col.key]) : ''
                sums[col.key] = { key: col.key, value }
            })
            return sums
        },

        buildParams () {
            const p = {
                date_preset:    this.model.date_preset,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
            if (this.isCustomRange) {
                p.date_from = this.model.date_from
                p.date_to   = this.model.date_to
            }
            if (this.model.branch_id && this.model.branch_id.length) p.branch_id = this.model.branch_id
            if (this.model.tran_id !== null) p.tran_id = this.model.tran_id
            if (this.model.user_id && this.model.user_id.length) p.user_id = this.model.user_id
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                p.search = this.searchQuery.trim()
            }
            return p
        },

        fetchData () {
            if (this.isCustomRange && (!this.model.date_from || !this.model.date_to)) {
                this.notice({ type: 'warning', text: 'Please pick a custom date range first.' })
                return
            }

            this.errors = new Errors()
            this.loading = true
            this.summaryLoading = true

            this.$store
                .dispatch('report/creditOperation/devicePostingLogV3', this.buildParams())
                .then(response => {
                    const data    = response.data || []
                    const meta    = response.meta || response
                    const summary = response.summary || null
                    const totals  = response.totals  || null

                    this.rows = data
                    this.meta = {
                        current_page: meta.current_page || 1,
                        last_page:    meta.last_page    || 1,
                        total:        meta.total ?? 0
                    }

                    if (summary) {
                        this.summary = {
                            all:       Object.assign({ count: 0, total: '0' }, summary.all       || {}),
                            repayment: Object.assign({ count: 0, total: '0' }, summary.repayment || {}),
                            recovery:  Object.assign({ count: 0, total: '0' }, summary.recovery  || {}),
                            users:     Object.assign({ count: 0 },             summary.users     || {})
                        }
                    }
                    if (totals) {
                        this.totals = Object.assign({}, this.totals, totals)
                    }
                })
                .catch(error => {
                    this.notice({ type: 'error', text: (error && error.message) || 'Failed to load report' })
                    this.errors = new Errors((error && error.errors) || {})
                    this.rows = []
                })
                .finally(() => {
                    this.loading = false
                    this.summaryLoading = false
                })
        },

        onUserQueryChange (q) {
            this.userQuery = q || ''
        },

        onCustomRangeChange (range) {
            if (Array.isArray(range) && range[0] && range[1]) {
                this.model.date_from = range[0]
                this.model.date_to   = range[1]
            } else {
                this.model.date_from = null
                this.model.date_to   = null
            }
        },

        onSearch () {
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onClearSearch () {
            const hadSearch = !!(this.searchQuery && this.searchQuery.trim())
            this.searchQuery = ''
            if (hadSearch && this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        },

        onReset () {
            this.searchQuery = ''
            this.model.date_preset = 'today'
            this.model.date_from = null
            this.model.date_to   = null
            this.model.branch_id = []
            this.model.tran_id = null
            this.model.user_id = []
            this.model.sort = 'transaction_date'
            this.model.sort_dir = 'desc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                this.model.sort = 'transaction_date'
                this.model.sort_dir = 'desc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) {
            this.model.page = page
            this.fetchData()
        },
        onPerPageChange (v) {
            this.perPage = v
            this.model.page = 1
            this.fetchData()
        },

        exportExcel () {
            if (this.isCustomRange && (!this.model.date_from || !this.model.date_to)) {
                this.notice({ type: 'warning', text: 'Please pick a custom date range first.' })
                return
            }
            this.errors = new Errors()
            this.exporting = true

            this.$store
                .dispatch('report/creditOperation/devicePostingLogV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        notice (not) {
            this.$Notice[not.type]({
                title: 'DEVICE POSTING LOG',
                desc: not.text
            })
        }
    },
    watch: {
        // Narrow the user dropdown as the branch picker changes. If no branch
        // is chosen we fall back to the full assigned-branch set.
        'model.branch_id': debounce(function (value) {
            this.model.user_id = []
            if (value && value.length) {
                this.getUserLogin({ branch_id: value })
            } else {
                this.loadUsersScoped()
            }
        }, 400)
    },
    mounted () {
        this._onResize = debounce(() => { this.viewportHeight = window.innerHeight }, 100)
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => { vm.loadUsersScoped() })
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: #6b7280; margin-bottom: 4px; height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }

.pill-group {
    display: inline-flex; align-items: center; flex-wrap: nowrap; gap: 3px;
    padding: 3px; background: #f3f4f6; border-radius: 6px;
    height: 34px; box-sizing: border-box; overflow-x: auto; max-width: 100%;
}
.pill {
    background: transparent; border: none; padding: 4px 10px;
    font-size: 12px; color: #4b5563; border-radius: 4px;
    cursor: pointer; transition: all 0.15s; line-height: 1.2;
    white-space: nowrap; display: inline-flex; align-items: center;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff; color: #111827; font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.chip-pill {
    display: inline-flex; align-items: center; padding: 2px 8px;
    font-size: 11px; border-radius: 10px;
    background: #f3f4f6; color: #374151; line-height: 1.5; white-space: nowrap;
}
.chip-default  { background: #f3f4f6; color: #374151; }
.chip-branch   { background: #eef2ff; color: #4338ca; }
.chip-currency { background: #ecfdf5; color: #047857; }
.chip-type     { background: #dbeafe; color: #1e40af; }
.chip-co       { background: #f3e8ff; color: #6b21a8; }
.chip-search   { background: #fce7f3; color: #9d174d; }

@media (max-width: 991px) {
    .filter-grid > .filter-cell { grid-column: span 12 !important; }
}

.search-group {
    display: flex; align-items: stretch; height: 34px;
    border: 1px solid #d1d5db; border-radius: 6px;
    overflow: hidden; background: #ffffff;
}
.search-input-wrap { position: relative; flex: 1; display: flex; align-items: stretch; min-width: 0; }
.search-input {
    flex: 1; border: none !important; height: 100% !important;
    padding: 0 32px 0 12px; outline: none; background: transparent;
    font-size: 13px; color: #111827; min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
    background: transparent; border: none; padding: 4px;
    font-size: 14px; color: #9ca3af; cursor: pointer;
}
.search-btn {
    background: #3b82f6; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.search-btn:hover:not(:disabled) { background: #2563eb; }
.search-btn:disabled { background: #93c5fd; cursor: wait; }
.reset-btn {
    background: #f9fafb; color: #6b7280; border: none;
    border-left: 1px solid #e5e7eb; padding: 0 12px; cursor: pointer;
}
.reset-btn:hover { background: #f3f4f6; color: #374151; }

.date-preview {
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
    padding-left: 8px;
    line-height: 34px;
    white-space: nowrap;
}

.results-card {
    background: #ffffff; border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); overflow: hidden;
}
.results-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid #e5e7eb;
    background: #ffffff; gap: 8px; flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
    padding: 10px 16px; border-top: 1px solid #e5e7eb;
    background: #f9fafb; gap: 8px; min-height: 52px;
}
.pagination-left {
    display: flex; align-items: center; gap: 10px;
    height: 28px; line-height: 28px; font-size: 13px; color: #6b7280;
}
.pagination-lbl { display: inline-flex; align-items: center; height: 28px; line-height: 28px; white-space: nowrap; }
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; line-height: 28px; }

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
@media (max-width: 991px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex; flex-direction: column; justify-content: space-between; gap: 8px;
    padding: 12px 14px; background: #ffffff;
    border: 1px solid #e5e7eb; border-top: 3px solid;
    border-radius: 6px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 110px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px; height: 22px; display: flex; align-items: center; justify-content: center;
    border-radius: 4px; font-size: 11px; flex-shrink: 0;
}
.summary-card__label {
    flex: 1; font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.03em; color: #6b7280; line-height: 1.2;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.summary-card__count {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    padding: 1px 6px; background: #f3f4f6; border-radius: 10px; line-height: 1.4;
}
.summary-card__value {
    display: flex; align-items: baseline; gap: 6px;
    line-height: 1; white-space: nowrap; overflow: hidden;
}
.summary-card__amount {
    font-size: 20px; font-weight: 700; color: #111827;
    overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.summary-card__currency {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    letter-spacing: 0.03em; flex-shrink: 0;
}
.summary-card--cur_due  { border-top-color: #6366f1; }
.summary-card--cur_due  .summary-card__icon { background: #eef2ff; color: #4338ca; }
.summary-card--cur_paid { border-top-color: #10b981; }
.summary-card--cur_paid .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--early    { border-top-color: #3b82f6; }
.summary-card--early    .summary-card__icon { background: #eff6ff; color: #1e40af; }
.summary-card--past_due { border-top-color: #ef4444; }
.summary-card--past_due .summary-card__icon { background: #fee2e2; color: #991b1b; }

::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px; color: #374151; font-weight: 600;
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
::v-deep .ivu-table-summary td {
    background: #f9fafb !important; font-weight: 700 !important;
    color: #111827 !important; border-top: 2px solid #d1d5db !important;
}
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

.export-group {
    display: inline-flex; align-items: stretch; height: 32px;
    border-radius: 5px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db; background: #ffffff;
}
.export-select {
    border: 0; border-right: 1px solid #d1d5db;
    background: #ffffff; padding: 0 28px 0 12px;
    font-size: 12px; color: #374151; font-weight: 500;
    cursor: pointer; outline: none; height: 100%;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat; background-position: right 10px center; background-size: 10px 6px;
}
.export-btn {
    background: #10b981; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }

.empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center; height: 100%; position: absolute; inset: 0;
}
</style>

<!-- Unscoped: render-function elements don't receive data-v-xxx. -->
<style>
.name-text {
    color: #111827; font-size: 12px; font-weight: 500;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.tran-badge {
    display: inline-block; padding: 1px 8px;
    font-size: 11px; font-weight: 600; border-radius: 10px;
    letter-spacing: 0.02em;
}
.tran-badge--rep { color: #047857; background: #ecfdf5; border: 1px solid #a7f3d0; }
.tran-badge--rec { color: #92400e; background: #fef3c7; border: 1px solid #fde68a; }

.phys-badge {
    display: inline-block; padding: 1px 8px;
    font-size: 11px; font-weight: 600; border-radius: 10px;
    letter-spacing: 0.02em;
}
.phys-badge--yes { color: #047857; background: #ecfdf5; border: 1px solid #a7f3d0; }
.phys-badge--no  { color: #9d174d; background: #fdf2f8; border: 1px solid #fbcfe8; }
</style>
